"use client";

import { useEffect, useRef, useState } from "react";

import { API_BASE } from "@/components/constants/general";
import type { Partner, PartnersMapFilters } from "@/types/partner";

const buildUrl = (filters?: PartnersMapFilters) => {
  const params = new URLSearchParams();
  if (filters?.state_id != null) params.set("state_id", String(filters.state_id));
  if (filters?.district_id != null) params.set("district_id", String(filters.district_id));
  if (filters?.taluka_id != null) params.set("taluka_id", String(filters.taluka_id));
  if (filters?.village_id != null) params.set("village_id", String(filters.village_id));
  if (filters?.type) params.set("type", filters.type);
  const qs = params.toString();
  return `${API_BASE}/api/partners/map${qs ? `?${qs}` : ""}`;
};

export function usePartnersMap(filters?: PartnersMapFilters) {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const abortRef = useRef<AbortController>();
  const filtersKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(buildUrl(JSON.parse(filtersKey)), {
          signal: ac.signal,
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        setPartners(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (e?.name !== "AbortError") setError(e?.message || "Failed to load partners");
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [filtersKey, refreshIndex]);

  const refetch = () => setRefreshIndex((n) => n + 1);

  return { partners, loading, error, refetch };
}
